"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useState, useRef } from "react";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import { getHomeBlurPlaceholder } from "@/utils/homeBlurPlaceholders";

export interface RoomGalleryItem {
  id: string;
  title: string;
  description: string;
  href: string;
  image: string;
  roomType:
    | "Family Suite"
    | "Double Room"
    | "Junior Suite"
    | "Standard Single"
    | "Triple Room"
    | "Balcony"
    | "View"
    | "Premium Double Room";
}

export interface RoomGalleryCarouselProps {
  title?: string;
  description?: string;
  items: RoomGalleryItem[];
}

const roomData: RoomGalleryItem[] = [
  // Heritage Family Suite - Jungfrau View
  {
    id: "family-suite",
    title: "Heritage Family Suite - Jungfrau View",
    description: "Spacious family suite with panoramic views and kitchenette",
    href: "/accommodations#family-suite",
    image:
      "/images/optimized rooms/Heritage Family Suite - Jungfrau View/HFS 1_mobile.webp",
    roomType: "Family Suite",
  },
  // Alpine Junior Suite - Panorama View
  {
    id: "junior-suite",
    title: "Alpine Junior Suite - Panorama View",
    description:
      "Luxurious junior suite with separate living area and mountain views",
    href: "/accommodations#junior-suite",
    image:
      "/images/optimized rooms/Alpine Juniour Suite - Panoroma View/Alp Jun Sui 1_mobile.webp",
    roomType: "Junior Suite",
  },
  // Classic Triple Room - Alpine Comfort
  {
    id: "standard-triple-room",
    title: "Classic Triple Room - Alpine Comfort",
    description: "Spacious triple room perfect for families or small groups",
    href: "/accommodations#triple-rooms",
    image:
      "/images/optimized rooms/Classic Triple Room - Alpine Comfort/classic Triple 1_mobile.webp",
    roomType: "Triple Room",
  },
  // Jungfrau Superior Double - Balcony & Lake View
  {
    id: "superior-double-room",
    title: "Jungfrau Superior Double - Balcony & Lake View",
    description: "Enhanced double room with superior amenities and extra space",
    href: "/accommodations#double-rooms",
    image:
      "/images/optimized rooms/Jungfrau Superior Double - Balcony and Lake view/JungSupDouble 1_mobile.webp",
    roomType: "Superior Double Room",
  },
  // Alpine Double/Twin - Balcony & Lake View
  {
    id: "alpine-double-twin-balcony-lake",
    title: "Alpine Double/Twin - Balcony & Lake View",
    description: "Flexible Alpine room with balcony and stunning lake views",
    href: "/accommodations#alpine-double-twin",
    image:
      "/images/optimized rooms/Alpine Double:Twin - Balcony & Lake view/Alp Double Balc 1_pc.webp",
    roomType: "Alpine Double/Twin - Balcony & Lake View",
  },
  // Alpine Classic Double/Twin - Village View
  {
    id: "alpine-classic-double-twin-village",
    title: "Alpine Classic Double/Twin - Village View",
    description:
      "Classic Alpine room with charming village views and flexible bedding",
    href: "/accommodations#alpine-classic-double-twin",
    image:
      "/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClaTwin 1_pc.webp",
    roomType: "Alpine Classic Double/Twin - Village View",
  },
  // Jungfrau Single - Balcony & Lake View
  {
    id: "jungfrau-single-balcony-lake",
    title: "Jungfrau Single - Balcony & Lake View",
    description: "Single room with private balcony and stunning lake views",
    href: "/accommodations#jungfrau-single",
    image:
      "/images/optimized rooms/Jungfrau Single - Balcony and Lake View/JungSingle 1_pc.webp",
    roomType: "Jungfrau Single - Balcony & Lake View",
  },
  // Heritage Single - Village View
  {
    id: "heritage-single-village",
    title: "Heritage Single - Village View",
    description:
      "Classic single room with charming village views and heritage character",
    href: "/accommodations#heritage-single",
    image:
      "/images/optimized rooms/Heritage Single - Village View/HS 1_pc.webp",
    roomType: "Heritage Single - Village View",
  },
  // Classic Single Room - Alpine Comfort
  {
    id: "standard-single",
    title: "Classic Single Room - Alpine Comfort",
    description: "Enhanced single room with premium amenities and extra space",
    href: "/accommodations#single-rooms",
    image:
      "/images/optimized rooms/Classic Single Room - Alpine Comfort/SingleComf 1_pc.webp",
    roomType: "Classic Single Room - Alpine Comfort",
  },
];

const RoomGalleryCarousel = ({
  title,
  description,
  items = roomData,
}: RoomGalleryCarouselProps) => {
  // Get language and translation function
  const { t, language } = useLanguage();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentSlideGroup, setCurrentSlideGroup] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Helper function to localize URLs with hash fragments
  const getLocalizedHref = (href: string): string => {
    // Split href into path and hash fragment
    const [path, hash] = href.split("#");
    // Localize the path part
    const localizedPath = getLocalizedUrl(path, language);
    // Recombine with hash if it exists
    return hash ? `${localizedPath}#${hash}` : localizedPath;
  };

  // Helper function to translate room titles and descriptions
  const translateRoomContent = (
    roomType: string,
    field: "title" | "description",
  ) => {
    const translations: Record<string, Record<string, string>> = {
      "Family Suite": {
        title: t("rooms.familySuite"),
        description: t("rooms.familySuiteDesc"),
      },
      "Junior Suite": {
        title: t("rooms.juniorSuite"),
        description: t("rooms.juniorSuiteDesc"),
      },
      "Triple Room": {
        title: t("rooms.tripleRoom"),
        description: t("rooms.tripleRoomDesc"),
      },
      "Superior Double Room": {
        title: t("rooms.premiumDouble"),
        description: t("rooms.premiumDoubleDesc"),
      },
      "Alpine Double/Twin - Balcony & Lake View": {
        title: t("rooms.alpineDoubleTwin"),
        description: t("rooms.alpineDoubleTwinDescription.desktop"),
      },
      "Alpine Classic Double/Twin - Village View": {
        title: t("rooms.alpineClassicDoubleTwin"),
        description: t("rooms.alpineClassicDoubleTwinDescription.desktop"),
      },
      "Jungfrau Single - Balcony & Lake View": {
        title: t("rooms.jungfrauSingle"),
        description: t("rooms.jungfrauSingleDescription.desktop"),
      },
      "Heritage Single - Village View": {
        title: t("rooms.heritageSingle"),
        description: t("rooms.heritageSingleDescription.desktop"),
      },
      "Classic Single Room - Alpine Comfort": {
        title: t("rooms.standardSingle"),
        description: t("rooms.standardSingleDesc"),
      },
    };

    return (
      translations[roomType]?.[field] || (field === "title" ? roomType : "")
    );
  };

  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      const selectedSlide = carouselApi.selectedScrollSnap();
      setCurrentSlide(selectedSlide);
      // Calculate which group of 3 slides is currently visible
      setCurrentSlideGroup(Math.floor(selectedSlide / 3));
    };
    updateSelection();
    carouselApi.on("select", updateSelection);
    return () => {
      carouselApi.off("select", updateSelection);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const container = carouselApi.containerNode();
    if (!container) return;

    let scrollAccumulator = 0;
    const scrollThreshold = 80; // Require more scroll distance before triggering

    const handleWheel = (e: WheelEvent) => {
      // Check if horizontal scroll is intended (shift+wheel or trackpad horizontal gesture)
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) {
        e.preventDefault();

        const scrollAmount = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        scrollAccumulator += scrollAmount;

        if (Math.abs(scrollAccumulator) >= scrollThreshold) {
          if (scrollAccumulator > 0) {
            carouselApi.scrollNext();
          } else {
            carouselApi.scrollPrev();
          }
          scrollAccumulator = 0; // Reset after triggering
        }
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [carouselApi]);

  // Video intersection observer - play when 70% in view
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            video.play().catch(console.error);
          } else {
            video.pause();
          }
        });
      },
      {
        threshold: 0.7, // Trigger when 70% of the video is visible
        rootMargin: "0px",
      },
    );

    observer.observe(video);

    return () => {
      observer.unobserve(video);
    };
  }, []);

  // Auto progress functionality
  useEffect(() => {
    if (!carouselApi || isHovered) return;

    const interval = setInterval(() => {
      if (carouselApi.canScrollNext()) {
        carouselApi.scrollNext();
      } else {
        carouselApi.scrollTo(0); // Reset to first slide
      }
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [carouselApi, isHovered]);

  // Safari video autoplay fix - Programmatic play trigger
  // Note: IntersectionObserver handles play/pause based on visibility
  // This ensures Safari can autoplay by attempting play() when metadata loads
  // IntersectionObserver will manage actual playback based on visibility
  useEffect(() => {
    const playVideo = async () => {
      if (!videoRef.current) return;

      try {
        // Always try to play when metadata loads (helps Safari autoplay policy)
        // IntersectionObserver will pause if not visible, which may cause AbortError (expected)
        await videoRef.current.play();
      } catch (error) {
        // Ignore AbortError - IntersectionObserver paused it (expected behavior)
        // Also ignore NotAllowedError - Safari's autoplay policy (IntersectionObserver will handle it)
        if (
          error instanceof DOMException &&
          error.name !== "AbortError" &&
          error.name !== "NotAllowedError"
        ) {
          console.log("Video autoplay prevented:", error);
        }
      }
    };

    // Try when video metadata is loaded (important for Safari autoplay policy)
    const video = videoRef.current;
    if (video) {
      video.addEventListener("loadedmetadata", playVideo);
      video.addEventListener("canplay", playVideo);
      return () => {
        video.removeEventListener("loadedmetadata", playVideo);
        video.removeEventListener("canplay", playVideo);
      };
    }
  }, []);

  return (
    <section
      className="py-20 bg-gradient-to-b from-elegant-white via-pebble-beige to-elegant-white"
      data-oid="6melv5_"
    >
      <div
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        data-oid="3sggxl1"
      >
        {/* Mobile Layout */}
        <div className="lg:hidden" data-oid="f-p.mya">
          {/* Video Section with Title Overlay */}
          <div className="mb-12" data-oid="3osm4u.">
            <div
              className="relative w-full aspect-[9/16] overflow-hidden rounded-xl"
              data-oid="l6s5vzg"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                controls={false}
                data-oid="kzigu5i"
              >
                <source
                  src="/videos/home-room-vid-optimized-vbr.mp4"
                  type="video/mp4"
                  data-oid="sdwn9zh"
                />
                Your browser does not support the video tag.
              </video>
              {/* Gradient overlay for better text readability */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/40"
                data-oid="g7pfi7w"
              ></div>
              {/* Title Overlay at Top */}
              <div
                className="absolute top-0 left-0 right-0 flex items-start justify-center pt-8 z-10"
                data-oid="7qoa3nd"
              >
                <div className="text-center px-4" data-oid="gllu9co">
                  <h2
                    className="font-playfair text-4xl md:text-5xl text-white uppercase"
                    data-oid="60.yp_i"
                  >
                    {title || t("rooms.title")}
                  </h2>
                </div>
              </div>
              {/* Explore Rooms Button Overlay at Bottom */}
              <div
                className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-8 z-10"
                data-oid="mmgeb7d"
              >
                <a
                  href={getLocalizedUrl("/accommodations", language)}
                  className="group"
                  data-oid="39:_h8n"
                >
                  <Button
                    className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full font-lato font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    data-oid="yga8px4"
                  >
                    {t("rooms.exploreRooms")}
                    <ArrowRight
                      className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                      data-oid="dv6q0n8"
                    />
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Description Only */}
          <div className="mb-8" data-oid="p_q-zsm">
            <p
              className="font-lato text-lg text-pine-green/70 leading-relaxed mb-4"
              data-oid="u.h5ekg"
            >
              From cozy doubles to spacious family suites, each room offers
              Swiss comfort with breathtaking Alpine views.
            </p>
            <ul
              className="font-lato text-base text-pine-green/70 space-y-2"
              data-oid="ckc8phd"
            >
              <li className="flex items-center" data-oid="9e0n3_b">
                <span
                  className="w-2 h-2 bg-alpine-gold rounded-full mr-3 flex-shrink-0"
                  data-oid="kl3_w58"
                ></span>
                {t("rooms.pineWoodInteriors")}
              </li>
              <li className="flex items-center" data-oid="i75nrs3">
                <span
                  className="w-2 h-2 bg-alpine-gold rounded-full mr-3 flex-shrink-0"
                  data-oid="qt0wh2r"
                ></span>
                {t("rooms.organicCottonSheets")}
              </li>
              <li className="flex items-center" data-oid="w3cn4fx">
                <span
                  className="w-2 h-2 bg-alpine-gold rounded-full mr-3 flex-shrink-0"
                  data-oid="s_8jwyo"
                ></span>
                {t("rooms.seniorFriendly")}
              </li>
              <li className="flex items-center" data-oid="_cy2efm">
                <span
                  className="w-2 h-2 bg-alpine-gold rounded-full mr-3 flex-shrink-0"
                  data-oid="w3.jxtp"
                ></span>
                {t("rooms.privateBalconies")}
              </li>
            </ul>
          </div>

          {/* Basic Carousel */}
          <div
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            data-oid="hcirdpw"
          >
            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: "start",
                loop: false,
              }}
              className="w-full"
              data-oid="yb-ftcp"
            >
              <CarouselContent data-oid="-ofn:.2">
                {items.map((item) => (
                  <CarouselItem
                    key={item.id}
                    className="md:basis-1/2 lg:basis-1/3"
                    data-oid="k._707x"
                  >
                    <a
                      href={getLocalizedHref(item.href)}
                      className="group rounded-xl"
                      data-oid=".2.8rq8"
                    >
                      <div
                        className="group relative h-64 overflow-hidden rounded-xl"
                        data-oid="5r8-qlo"
                      >
                        <OptimizedImage
                          src={item.image}
                          alt={translateRoomContent(item.roomType, "title")}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          responsive={true}
                          priority={false}
                          fetchPriority="auto"
                          blurDataURL={getHomeBlurPlaceholder(item.image)}
                          data-oid="cerom.d"
                        />

                        {/* Alpine-themed gradient overlay */}
                        <div
                          className="absolute inset-0 h-full bg-gradient-to-t from-pine-green/90 via-pine-green/40 to-transparent mix-blend-multiply"
                          data-oid="t:fgmi_"
                        />

                        {/* Title at top */}
                        <div
                          className="absolute top-4 left-4 right-4"
                          data-oid="rs97wv_"
                        >
                          <h3
                            className="font-semibold font-playfair text-elegant-white text-lg"
                            data-oid="eus0ssd"
                          >
                            {translateRoomContent(item.roomType, "title")}
                          </h3>
                        </div>

                        {/* Description and explore link at bottom */}
                        <div
                          className="absolute bottom-4 left-4 right-4 text-elegant-white"
                          data-oid="b0e0n2y"
                        >
                          <p
                            className="text-sm opacity-90 font-lato mb-2 line-clamp-2"
                            data-oid="m2vg32z"
                          >
                            {translateRoomContent(item.roomType, "description")}
                          </p>
                          <div
                            className="flex items-center text-sm font-lato font-medium text-elegant-white/90 hover:text-alpine-gold transition-colors duration-300"
                            data-oid="o4wzkbb"
                          >
                            {t("rooms.exploreRoom")}{" "}
                            <ArrowRight
                              className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                              data-oid="9bp5vbp"
                            />
                          </div>
                        </div>
                      </div>
                    </a>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {/* Pagination Dots - Mobile/Tablet Only */}
            <div className="flex justify-center gap-2 mt-4" data-oid="ew:hp-g">
              {Array.from({ length: Math.ceil(items.length / 3) }).map(
                (_, index) => (
                  <button
                    key={index}
                    onClick={() => carouselApi?.scrollTo(index * 3)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentSlideGroup === index
                        ? "bg-alpine-gold scale-125"
                        : "bg-gray-300 hover:bg-gray-400"
                    }`}
                    aria-label={`Go to slide group ${index + 1}`}
                    data-oid="whqkz51"
                  />
                ),
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout - Bento Box Grid with Video on Right */}
        <div className="hidden lg:block" data-oid="q88_qms">
          {/* Bento Box Grid with Video on Right */}
          <div
            className="grid lg:grid-cols-[1fr_420px] lg:gap-4 lg:items-end"
            data-oid="_9cmbsu"
          >
            {/* Left Column: Description + Carousel */}
            <div
              className="flex flex-col justify-between h-full"
              data-oid="sn-xt9w"
            >
              {/* Title, Description and Features - At Top */}
              <div className="px-6" data-oid="_rfo6v1">
                <h2
                  className="font-playfair text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4 uppercase"
                  data-oid="uuefggp"
                >
                  {title || t("rooms.title")}
                </h2>
                <p
                  className="font-lato text-lg text-gray-900 leading-relaxed mb-4"
                  data-oid="lzkltfw"
                >
                  {description || t("rooms.description")}
                </p>
                <ul
                  className="font-lato text-base text-gray-900 space-y-2"
                  data-oid="3-5n6dh"
                >
                  <li className="flex items-center" data-oid="em4-yjh">
                    <span
                      className="w-2 h-2 bg-alpine-gold rounded-full mr-3 flex-shrink-0"
                      data-oid="zaj6nkt"
                    ></span>
                    {t("rooms.pineWoodInteriors")}
                  </li>
                  <li className="flex items-center" data-oid="_imnjtc">
                    <span
                      className="w-2 h-2 bg-alpine-gold rounded-full mr-3 flex-shrink-0"
                      data-oid="h.m0w-v"
                    ></span>
                    {t("rooms.privateBalconies")}
                  </li>
                  <li className="flex items-center" data-oid="myn4ht:">
                    <span
                      className="w-2 h-2 bg-alpine-gold rounded-full mr-3 flex-shrink-0"
                      data-oid=".4cwm1-"
                    ></span>
                    {t("rooms.seniorFriendly")}
                  </li>
                </ul>
              </div>

              {/* Pagination Controls - Between Text and Carousel */}
              <div className="mb-3 flex gap-2 px-6" data-oid="r-9g3u8">
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => carouselApi?.scrollPrev()}
                  disabled={!canScrollPrev}
                  className="border border-gray-300 hover:border-gray-400 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  data-oid="0dip4r3"
                >
                  <ArrowLeft className="h-4 w-4" data-oid="hfg:umj" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => carouselApi?.scrollNext()}
                  disabled={!canScrollNext}
                  className="border border-gray-300 hover:border-gray-400 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  data-oid="ry7ou3-"
                >
                  <ArrowRight className="h-4 w-4" data-oid="fc_1nr2" />
                </Button>
              </div>

              {/* Carousel Section - At Bottom */}
              <div className="flex flex-col" data-oid="x0y062b">
                {/* Carousel - Fixed height for uniform cards */}
                <div
                  className="relative h-[400px]"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  data-oid="41o02xc"
                >
                  <Carousel
                    setApi={setCarouselApi}
                    opts={{
                      align: "start",
                      loop: false,
                    }}
                    className="w-full h-full"
                    data-oid="azxv5n9"
                  >
                    <CarouselContent className="h-full" data-oid="p78pb:-">
                      {items.map((item) => (
                        <CarouselItem
                          key={item.id}
                          className="basis-1/3 h-[400px]"
                          data-oid="72vyi11"
                        >
                          <a
                            href={getLocalizedHref(item.href)}
                            className="group rounded-xl"
                            data-oid="hgc77:j"
                          >
                            <div
                              className="group relative h-[400px] w-full overflow-hidden rounded-xl"
                              data-oid="2i6cb9i"
                            >
                              <OptimizedImage
                                src={item.image}
                                alt={translateRoomContent(
                                  item.roomType,
                                  "title",
                                )}
                                className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                                responsive={true}
                                priority={false}
                                fetchPriority="auto"
                                blurDataURL={getHomeBlurPlaceholder(item.image)}
                                data-oid="ngakrva"
                              />

                              {/* Alpine-themed gradient overlay */}
                              <div
                                className="absolute inset-0 bg-gradient-to-t from-pine-green/90 via-pine-green/40 to-transparent mix-blend-multiply w-[255px] h-[416px]"
                                data-oid="6ls5qpa"
                              />

                              {/* Title at top */}
                              <div
                                className="absolute top-4 left-4 right-4"
                                data-oid="fa_2i6m"
                              >
                                <div
                                  className="text-xl font-semibold font-playfair text-elegant-white"
                                  data-oid="pc--ygz"
                                >
                                  {translateRoomContent(item.roomType, "title")}
                                </div>
                              </div>

                              <div
                                className="absolute inset-x-0 bottom-0 flex flex-col items-start p-6 text-elegant-white"
                                data-oid="ej7bt0i"
                              >
                                <div
                                  className="mb-4 font-lato text-elegant-white/90 leading-relaxed transition-all duration-300 group-hover:line-clamp-none line-clamp-2"
                                  data-oid="y8ctsyp"
                                >
                                  {translateRoomContent(
                                    item.roomType,
                                    "description",
                                  )}
                                </div>
                                <div
                                  className="flex items-center text-sm font-lato font-medium group-hover:text-alpine-gold transition-colors duration-300"
                                  data-oid="05bz__d"
                                >
                                  {t("rooms.exploreRoom")}{" "}
                                  <ArrowRight
                                    className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                                    data-oid="rlx3l3h"
                                  />
                                </div>
                              </div>
                            </div>
                          </a>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>
                </div>
              </div>
            </div>

            {/* Right Column: Video */}
            <div
              className="relative w-full aspect-[9/16] overflow-hidden rounded-xl"
              data-oid="qnks0g4"
            >
              <video
                ref={videoRef}
                className="object-cover h-[784px] w-[437px]"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                controls={false}
                data-oid="4ezh558"
              >
                <source
                  src="/videos/home-room-vid-optimized-vbr.mp4"
                  type="video/mp4"
                  data-oid="yu8j0u0"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { RoomGalleryCarousel };
